import React, { useState, useEffect } from 'react';
import config from '../config';
import AnalysisViewModal from './AnalysisViewModal';
import BackButton from './BackButton';

export default function SavedWorkspaceComparisons({ setPage }) {
    const [workspaceComparisons, setWorkspaceComparisons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [selectedName, setSelectedName] = useState('');

    useEffect(() => {
        loadWorkspaceComparisons();
    }, []);

    const loadWorkspaceComparisons = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${config.API_URL}/api/saved-workspace-comparisons?token=${token}`);
            const data = await response.json();
            setWorkspaceComparisons(data);
        } catch (error) {
            console.error('Error loading workspace comparisons:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenWorkspace = (comparison) => {
    // Save to localStorage so ComparisonWorkspace can load it
    const workspaceData = {
        patents: [], // Will be loaded via API
        patentNumbers: comparison.patent_numbers,
        yourPatentNumber: comparison.your_patent_number,
        ai_analysis: comparison.ai_analysis,
        loadFromSaved: true,
        savedName: comparison.name
    };
    localStorage.setItem('comparisonWorkspaceLoad', JSON.stringify(workspaceData));
    setPage('comparison-workspace');
};

    const handleViewAnalysis = (comparison) => {
        setSelectedAnalysis(comparison.ai_analysis);
        setSelectedName(comparison.name);
        setShowAnalysisModal(true);
    };

    const handleDelete = async (comparisonId) => {
        if (!window.confirm('Delete this workspace comparison?')) return;

        const token = localStorage.getItem('token');
        try {
            await fetch(`${config.API_URL}/api/saved-workspace-comparisons/${comparisonId}?token=${token}`, {
                method: 'DELETE'
            });
            loadWorkspaceComparisons();
        } catch (error) {
            alert('Error deleting: ' + error.message);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: 'white' }}>
            <header style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <BackButton onClick={() => setPage('search')} />
                    <img
                        src="/logo.svg"
                        alt="PatTrak Logo"
                        style={{ height: '32px', cursor: 'pointer' }}
                        onClick={() => setPage('search')}
                    />
                </div>
            </header>

            <main style={{ maxWidth: '900px', margin: '80px auto', padding: '0 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1d2934', margin: 0 }}>
                        Saved Workspace Comparisons
                    </h1>
                    <button
                        onClick={() => setPage('comparison-workspace')}
                        style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600'
                        }}
                    >
                        + New Comparison
                    </button>
                </div>

                {loading ? (
                    <p style={{ fontSize: '16px', color: '#64748b' }}>Loading...</p>
                ) : workspaceComparisons.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>📊</div>
                        <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                            No Saved Comparisons Yet
                        </h3>
                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                            Create cross-organization patent comparisons and save them for later
                        </p>
                        <button
                            onClick={() => setPage('comparison-workspace')}
                            style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600'
                            }}
                        >
                            Create Your First Comparison
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {workspaceComparisons.map((comparison) => (
                            <div
                                key={comparison.id}
                                style={{
                                    border: '2px solid #e2e8f0',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    background: 'white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                }}
                            >
                                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#1d2934' }}>
                                    {comparison.name}
                                </h3>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    {comparison.patent_numbers.map((pn, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                backgroundColor: pn === comparison.your_patent_number ? '#fef3c7' : '#f1f5f9',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: pn === comparison.your_patent_number ? '600' : '500',
                                                color: pn === comparison.your_patent_number ? '#92400e' : '#334155',
                                                border: pn === comparison.your_patent_number ? '1px solid #fbbf24' : 'none'
                                            }}
                                        >
                                            {pn === comparison.your_patent_number && '★ '}US{pn}
                                        </span>
                                    ))}
                                </div>

                                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                    Saved: {new Date(comparison.created_at).toLocaleDateString()} • 
                                    {comparison.ai_analysis ? ' Includes AI Analysis' : ' No AI Analysis'}
                                </p>

                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        onClick={() => handleOpenWorkspace(comparison)}
                                        style={{
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Open Workspace
                                    </button>

                                    {comparison.ai_analysis && (
                                        <button
                                            onClick={() => handleViewAnalysis(comparison)}
                                            style={{
                                                background: '#8b5cf6',
                                                color: 'white',
                                                border: 'none',
                                                padding: '10px 20px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            View Analysis
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleDelete(comparison.id)}
                                        style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                               )}
            </main>
            {/* Analysis View Modal */}
            <AnalysisViewModal
                show={showAnalysisModal}
                onClose={() => setShowAnalysisModal(false)}
                analysis={selectedAnalysis}
                workspaceName={selectedName}
            />
        </div>
    );
}
